import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const events = [
  {
    id: 'retail-investors-expo',
    title: 'India Retail Investors Expo',
    category: 'Expo',
    description: 'India\'s premier platform connecting retail investors with investment opportunities, featuring leading financial institutions and wealth management solutions.',
    image: 'https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?crop=entropy&cs=srgb&fm=jpg&q=85',
    date: 'February 2025',
    location: 'HITEX, Hyderabad',
    attendees: '10,000+',
    link: '/events/retail-investors-expo'
  },
  {
    id: 'finrun',
    title: 'The FinRun',
    category: 'Live Event',
    description: 'A unique marathon event that combines fitness with financial awareness, bringing together running enthusiasts and finance professionals for a cause.',
    image: 'https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85',
    date: 'March 2025',
    location: 'Hyderabad',
    attendees: '5,000+',
    link: '/events/finrun'
  }
];

export const EventsSection = () => {
  return (
    <section className="py-20 lg:py-32 bg-muted" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-16">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block text-gold font-semibold uppercase tracking-wider text-sm mb-4"
            >
              Our Flagship Events
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy"
            >
              Events That Define Industries
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link 
              to="/events" 
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-events-button"
            >
              All Events
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        {/* Events Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              data-testid={`event-card-${event.id}`}
            >
              <Link to={event.link} className="group block">
                <div className="relative h-[400px] lg:h-[500px] overflow-hidden">
                  {/* Image */}
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 event-overlay" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-6 left-6">
                    <span className="bg-gold text-navy text-sm font-semibold px-4 py-2 uppercase tracking-wider">
                      {event.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="font-heading text-2xl lg:text-3xl text-white mb-3 group-hover:text-gold transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-white/80 mb-6 line-clamp-2">
                      {event.description}
                    </p>

                    {/* Event Meta */}
                    <div className="flex flex-wrap gap-6 text-white/70 text-sm mb-6">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gold" />
                        {event.date}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gold" />
                        {event.location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gold" />
                        {event.attendees} Attendees
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="inline-flex items-center gap-2 text-gold font-semibold uppercase tracking-wider group-hover:gap-4 transition-all">
                      Explore Event
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
